import React, { useCallback, useEffect } from 'react'
import { useAppDispatch, useAppSelector } from '../hooks'
import { View, ScrollView, ActivityIndicator, Text, TouchableOpacity, StyleSheet } from 'react-native'

import { Error } from '../components/Utils'
import RadioButton from '../components/Utils/RadioButton'
import { t } from 'ttag'
import { useAppTheme } from '#app/theme'
import ButtonForm from '#app/components/Form/ButtonForm'

import { ScreenProps, SlaveConfigCurrentTransformerFormData } from '#app/types'
import { selectSlaveClampTypeForEdit, updateSlaveCurrentTransformer, slaveConfigClampTypeList } from '#app/slave'
import { selectLoaderById, useLoaderError, useLoaderSuccess } from '#app/loader'

import { Controller, SubmitHandler, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as Yup from 'yup'

const SlaveClamp = ({ navigation, route: { params: { id } } }: ScreenProps<'SlaveDetail'>) => {
  const theme = useAppTheme()

  const { defaultValues, clampTypes } = useAppSelector(state => selectSlaveClampTypeForEdit(state, { id }))
  const { control, handleSubmit, formState: { isDirty } } = useForm<SlaveConfigCurrentTransformerFormData>({
    defaultValues,
    resolver: yupResolver(schema)
  })

  const dispatch = useAppDispatch()
  const onSubmit = useCallback<SubmitHandler<SlaveConfigCurrentTransformerFormData>>((data) => {
    dispatch(updateSlaveCurrentTransformer({ id, data }))
  }, [dispatch, id])
  useEffect(() => {
    navigation.getParent()?.setOptions({ gestureEnabled: !isDirty })
    navigation.setOptions({ gestureEnabled: !isDirty })
  }, [navigation, isDirty])

  const loader = useAppSelector(state => selectLoaderById(state, { id: 'slave/updateCurrentTransformer' }))
  const { isLoading, message } = loader
  const onSuccess = useCallback(() => {
    navigation.goBack()
  }, [navigation])
  useLoaderSuccess(loader, onSuccess)
  const showError = useLoaderError(loader)

  useEffect(() => {
    navigation.setOptions({
      headerRight: () => isLoading ? <ActivityIndicator /> : null
    })
  }, [navigation, isLoading])

  return (
    <ScrollView contentInsetAdjustmentBehavior="automatic">
      <View style={theme.form.area}>
        {showError &&
          <View style={theme.form.formGroup}>
            <Error>{message}</Error>
          </View>
        }

        <Text style={[styles.description, { color: theme.textColor70 }]}>
          {t`Select the current transformer (CT) clamp rating that matches the clamp physically installed on the device.`}
        </Text>

        <Text style={[styles.label, { color: theme.textColor70 }]}>{t`Clamp Rating`}</Text>

        <Controller
          control={control}
          name="clampType"
          render={({ field: { onChange, value } }) =>
            <View style={[styles.optionList, { borderColor: theme.border.borderColor }]}>
              {clampTypes.map((item, idx) => {
                const itemValue = item.value === '' ? null : item.value
                const isSelected = value === itemValue
                return (
                  <TouchableOpacity
                    key={`${idx}`}
                    style={[
                      styles.optionRow,
                      { borderBottomColor: theme.border.borderColor },
                      idx === clampTypes.length - 1 && styles.optionRowLast,
                      isSelected && { backgroundColor: theme.colors.primary + '18' }
                    ]}
                    onPress={() => onChange(itemValue)}
                    activeOpacity={0.6}
                  >
                    <Text style={[styles.optionLabel, { color: isSelected ? theme.colors.primary : theme.textColor }]}>
                      {item.label}
                    </Text>
                    <RadioButton
                      size={16}
                      isSelected={isSelected}
                      innerColor={theme.colors.primary}
                      outerColor={isSelected ? theme.colors.primary : theme.textColor50}
                    />
                  </TouchableOpacity>
                )
              })}
            </View>
          }
        />

        <ButtonForm
          title={t`Send`}
          loading={isLoading}
          onPress={handleSubmit(onSubmit)}
          color={theme.colors.primary}
          btnStyle={styles.sendButton}
        />
      </View>
    </ScrollView>
  )
}

const styles = StyleSheet.create({
  description: {
    fontSize: 14,
    lineHeight: 20,
    marginBottom: 20
  },
  label: {
    fontSize: 12,
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: 8
  },
  optionList: {
    borderRadius: 10,
    borderWidth: 1,
    overflow: 'hidden',
    marginBottom: 20
  },
  optionRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 14,
    borderBottomWidth: StyleSheet.hairlineWidth
  },
  optionRowLast: {
    borderBottomWidth: 0
  },
  optionLabel: {
    fontSize: 16
  },
  sendButton: {
    height: 48,
    borderRadius: 8
  }
})

export default SlaveClamp

const schema: Yup.SchemaOf<SlaveConfigCurrentTransformerFormData> = Yup.object({
  clampType: Yup.mixed().oneOf(slaveConfigClampTypeList).nullable().defined()
}).required()
